import * as d3 from "d3";
import { createPlotData } from "@/components/VivoPlots/functions/createPlotData";
import { createOrUpdateScales } from "@/components/VivoPlots/functions/createOrUpdateScale";
import { addLabels } from "@/components/VivoPlots/functions/addLabels";
import { drawAxes } from "@/components/VivoPlots/functions/addAxes";
import { createOrUpdateSVG } from "@/components/VivoPlots/functions/addSvg";
import { createPlotConfig } from "@/components/VivoPlots/types";
import { drawNoDataMessage } from "@/components/VivoPlots/functions/drawPlotData/drawNoData";
import { drawData } from "@/components/VivoPlots/functions/drawPlotData";
import { getPlotDimensions } from "./utils/getPlotDimensions";
import { EMPTY_TABLE_VIEW } from "./plotTableView";
import {
  applyTickVisibility,
  resolveTickVisibility,
} from "./utils/tickVisibility";

export const createPlot = async ({
  data,
  type,
  scales,
  config: plotConfig,
}: createPlotConfig) => {
  const clearPlotDataLayers = (plotClassName: string) => {
    const svg = d3.select(`svg.${plotClassName}`);
    svg
      .selectAll(
        "g.bars, g.scatter, g.swarm, g.box, .connection-line, g.between-plot-panel",
      )
      .remove();
  };

  const hasLegend = scales.find(
    (scale) => scale.type === "fill" && scale.dataMapping,
  );
  plotConfig.showLegend = hasLegend && plotConfig.showLegend;

  const plotData = createPlotData(data, type, scales, plotConfig);
  const isNonRenderable =
    Boolean(plotConfig.noPlot) ||
    plotData.noData ||
    plotData.notPlottable ||
    !data?.length;

  if (plotData.maxLabelLength !== undefined) {
    plotConfig.maxLabelLength = plotData.maxLabelLength;
  }

  // getPlotDimensions reads this to size the bottom margin for rotated labels,
  // so it has to reach the config before dimensions are computed below.
  if (plotData.maxCategoryLabelLength !== undefined) {
    plotConfig.maxCategoryLabelLength = plotData.maxCategoryLabelLength;
  }

  // Determine if any eligible subgroup scales exist.
  // Eligibility: mapping is defined, not "none", and differs from the
  // data mappings used by the primary axes (left/bottom positioned scales).
  const axisDataMappings = new Set(
    scales
      .filter(
        (scale) => scale.position === "left" || scale.position === "bottom",
      )
      .map((scale) => scale.dataMapping)
      .filter(Boolean),
  );

  const hasSubgroups = scales.some(
    (scale) =>
      scale.type === "subgroup" &&
      !!scale.dataMapping &&
      scale.dataMapping !== "none" &&
      !axisDataMappings.has(scale.dataMapping),
  );
  plotConfig.hasSubgroups = hasSubgroups;

  const dimensions = getPlotDimensions(plotConfig.plotClassName, plotConfig);

  const shouldClearSvg = type !== "swarm-box";
  createOrUpdateSVG(plotConfig?.plotClassName, dimensions, shouldClearSvg);

  if (!shouldClearSvg) {
    const svg = d3.select(`svg.${plotConfig.plotClassName}`);
    svg
      .selectAll(
        ".labels, .filter-button-svg, .x-axis, .y-axis, .subgroup-axis, .no-data-icon, .no-data-message, g.bars, g.scatter",
      )
      .remove();
  }

  if (isNonRenderable) {
    clearPlotDataLayers(plotConfig.plotClassName);
    addLabels(plotConfig.labels, plotConfig.plotClassName, plotConfig);
    drawNoDataMessage(
      plotConfig.plotClassName,
      dimensions,
      plotConfig.noPlot?.text,
    );
    return {
      plotScales: [],
      tableView: EMPTY_TABLE_VIEW,
    };
  }

  const plotScales = createOrUpdateScales(plotData.scales, dimensions);
  const plotResult = {
    plotScales,
    tableView: plotData.tableView,
    ...(plotData.pairingStats ? { pairingStats: plotData.pairingStats } : {}),
  };

  addLabels(plotConfig.labels, plotConfig.plotClassName, plotConfig);
  drawAxes(plotConfig.plotClassName, plotScales, dimensions, {
    verticalXAxisLabels: plotConfig.verticalXAxisLabels,
  });
  if (plotConfig.noAxisPath) {
    d3.select(`svg.${plotConfig.plotClassName}`).selectAll("path").remove();
  }
  applyTickVisibility(
    plotConfig.plotClassName,
    resolveTickVisibility(plotConfig),
  );

  await drawData(
    plotConfig.plotClassName,
    plotData,
    plotScales,
    plotConfig,
    dimensions,
    type,
  );

  return plotResult;
};
